// Changes between two flow snapshots, GitHub style: only the parts that differ, each with a line diff.

const isObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

export const deepEqual = (a, b) => {
  if (a === b) return true;
  if (Array.isArray(a)) {
    return Array.isArray(b) && a.length === b.length && a.every((item, index) => deepEqual(item, b[index]));
  }
  if (isObject(a) && isObject(b)) {
    const keys = Object.keys(a);
    return keys.length === Object.keys(b).length && keys.every(key => key in b && deepEqual(a[key], b[key]));
  }
  return false;
};

// Node fields that only move or size the node on the canvas
export const LAYOUT_KEYS = new Set(["position", "positionAbsolute", "width", "height", "selected", "dragging", "measured"]);

const idOf = (item) => isObject(item) && item.id !== undefined && item.id !== null ? String(item.id) : null;

// Name shown for an item with an id in a change path (node, intent ...), else its key
const labelOf = (value, key) => {
  if (idOf(value) === null) return key;
  const name = value.data?.name || value.data?.label || value.name || value.label;
  return typeof name === "string" && name.trim() ? name : key;
};

// Lists where every item has a unique id are matched by id, so an inserted or reordered item is one change
const matchesById = (before, after) => {
  const ids = (list) => list.map(idOf);
  const unique = (list) => !list.includes(null) && new Set(list).size === list.length;
  return before.length + after.length > 0 && unique(ids(before)) && unique(ids(after));
};

// Returns [{path: [{key, label}], kind: "added" | "removed" | "changed", before, after}], down to the smallest part
// that differs. Lists without ids are compared as a whole (the line diff shows what moved inside them).
// ignoreKeys: object keys left out of the comparison (e.g. LAYOUT_KEYS)
export const diffJson = (before, after, {ignoreKeys} = {}, path = [], changes = []) => {
  if (deepEqual(before, after)) return changes;
  if (before === undefined) {
    changes.push({path, kind: "added", after});
    return changes;
  }
  if (after === undefined) {
    changes.push({path, kind: "removed", before});
    return changes;
  }
  const step = (key, b, a) =>
    diffJson(b, a, {ignoreKeys}, [...path, {key, label: labelOf(a ?? b, key)}], changes);

  if (isObject(before) && isObject(after)) {
    const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])]
      .filter(key => !ignoreKeys?.has(key));
    keys.forEach(key => step(key, before[key], after[key]));
    return changes;
  }
  if (Array.isArray(before) && Array.isArray(after) && matchesById(before, after)) {
    const beforeById = new Map(before.map(item => [idOf(item), item]));
    const afterById = new Map(after.map(item => [idOf(item), item]));
    // new order first, then the removed items
    const ids = [...afterById.keys(), ...[...beforeById.keys()].filter(id => !afterById.has(id))];
    ids.forEach(id => step(id, beforeById.get(id), afterById.get(id)));
    return changes;
  }
  changes.push({path, kind: "changed", before, after});
  return changes;
};

// Strings are shown as text (prompts and messages diff per line), anything else as formatted JSON
export const toLines = (value) => {
  if (value === undefined) return [];
  if (typeof value === "string") return value.split("\n");
  return JSON.stringify(value, null, 2).split("\n");
};

// Line diff (longest common subsequence): [{type: " " | "-" | "+", text}]
const MAX_LCS_CELLS = 2000000;
export const diffLines = (before, after) => {
  const n = before.length;
  const m = after.length;
  if (n * m > MAX_LCS_CELLS) {
    return [...before.map(text => ({type: "-", text})), ...after.map(text => ({type: "+", text}))];
  }
  const lengths = Array.from({length: n + 1}, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lengths[i][j] = before[i] === after[j]
        ? lengths[i + 1][j + 1] + 1
        : Math.max(lengths[i + 1][j], lengths[i][j + 1]);
    }
  }
  const lines = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (before[i] === after[j]) {
      lines.push({type: " ", text: before[i]});
      i++;
      j++;
    } else if (lengths[i + 1][j] >= lengths[i][j + 1]) {
      lines.push({type: "-", text: before[i++]});
    } else {
      lines.push({type: "+", text: after[j++]});
    }
  }
  while (i < n) lines.push({type: "-", text: before[i++]});
  while (j < m) lines.push({type: "+", text: after[j++]});
  return lines;
};

// Keeps `context` unchanged lines around each change; longer unchanged runs become {type: "skip", count}
export const collapseContext = (lines, context = 3) => {
  const near = lines.map(() => false);
  lines.forEach((line, index) => {
    if (line.type === " ") return;
    for (let k = Math.max(0, index - context); k <= Math.min(lines.length - 1, index + context); k++) near[k] = true;
  });
  const result = [];
  lines.forEach((line, index) => {
    if (near[index]) {
      result.push(line);
    } else if (result[result.length - 1]?.type === "skip") {
      result[result.length - 1].count++;
    } else {
      result.push({type: "skip", count: 1});
    }
  });
  return result;
};

// Lines of one change: everything added, everything removed, or the line diff of both sides
export const changeLines = (change) => {
  if (change.kind === "added") return toLines(change.after).map(text => ({type: "+", text}));
  if (change.kind === "removed") return toLines(change.before).map(text => ({type: "-", text}));
  return collapseContext(diffLines(toLines(change.before), toLines(change.after)));
};
